import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { commonReducers, setReduxStoreForHttpClient } from '@eo/shared';

import { adminController } from '../pages/AdminOverview/controller';
import { clientDetailController } from '../pages/ClientDetail/controller';
import { userDetailController } from '../pages/UserDetail/controller';
import { capacityController } from '../pages/Capacity/controller';
import { reviewBoardController } from '../pages/ReviewBoard/controller';
import { settingsController } from '../pages/Settings/controller';
import { timeTrackingController } from '../pages/TimeTracking/controller';
import { staffReducer } from '../staff/slice';

// Page controllers carry their own slice reducers — register every one here.
// Each entry's key matches the controller's `name`; <Page> reads the slice
// by name when rendering.
const rootReducer = combineReducers({
  ...commonReducers,
  staff: staffReducer,
  [reviewBoardController.name]: reviewBoardController.reducer,
  [timeTrackingController.name]: timeTrackingController.reducer,
  [settingsController.name]: settingsController.reducer,
  [adminController.name]: adminController.reducer,
  [clientDetailController.name]: clientDetailController.reducer,
  [userDetailController.name]: userDetailController.reducer,
  [capacityController.name]: capacityController.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: {
        // `meta.arg.extra.navigate` is the react-router navigate fn that
        // useDispatchActions injects so success handlers can redirect. It
        // never reaches state, so the check is noise for every async action.
        ignoredActionPaths: ['payload.headers', 'meta.arg.extra.navigate'],
      },
    }),
});

// Register the store with the http-client registry. The 'api' client itself is
// created in bootstrap.ts (per FRONTEND.md §3 — createHttpClient lives there
// and only there).
setReduxStoreForHttpClient(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

// Typed wrappers — every component / controller imports these instead of the
// untyped `useDispatch` / `useSelector` from react-redux. Per FRONTEND.md §9.
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
